// SegmentEditor.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { SlControlPlay, SlControlForward, SlControlRewind } from "react-icons/sl";
import PitchEditorChart from "./intonation/PitchEditorChart";

/**
 * SegmentEditor + Narrator pitch fetch for EDITOR
 *
 * ✅ Calls:
 * POST /analysis/edit_narrator/{owner}/{language}/{author}/{title}/{file}/{start}/{end}/{type}
 *
 * ✅ The editor endpoint returns an untrimmed pitch curve
 * ✅ Refetches when the selected segment start/end changes
 * ✅ Word text edits and internal word-boundary drags reuse the current pitch
 * ✅ The browser audio element contains only the currently selected fragment.
 * ✅ Segment/word timestamps remain absolute for editing and saving.
 * ✅ Playback converts absolute timestamps to fragment-relative currentTime.
 * ✅ PitchEditorChart receives an offset proxy that preserves its former
 *    full-audio/absolute-time contract.
 *
 * FIXES INCLUDED:
 * ✅ Accurate playback stop/loop (requestAnimationFrame instead of timeupdate)
 * ✅ Playback speed options: 1.0, 0.5, 0.25
 * ✅ Word-boundary drag can move BOTH left and right
 * ✅ Split segment at a word into two segments
 * ✅ Undo/Redo covers split as well
 */

const MIN_SEG_DUR = 0.05;
const MIN_WORD_DUR = 0.02;
const DEFAULT_NEW_WORD_LEN = 0.2;

const isFiniteNum = (v) => Number.isFinite(Number(v));
const clamp = (x, lo, hi) => Math.max(lo, Math.min(x, hi));

function deepCloneSegments(segs) {
  return (segs ?? []).map((s) => ({
    ...s,
    words: (s.words ?? []).map((w) => ({ ...w })),
  }));
}

function rebuildSegmentText(seg) {
  return (seg.words ?? [])
    .map((w) => (w.text ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

function sanitizeSegmentTimes(seg) {
  const s = { ...seg };
  let st = Number(s.start);
  let en = Number(s.end);

  if (!Number.isFinite(st)) st = 0;
  if (!Number.isFinite(en)) en = st + MIN_SEG_DUR;
  if (en <= st + MIN_SEG_DUR) en = st + MIN_SEG_DUR;

  s.start = st;
  s.end = en;
  return s;
}

function ensureWordValid(w) {
  const ww = { ...w };
  let ws = Number(ww.start);
  let we = Number(ww.end);

  if (!Number.isFinite(ws)) ws = 0;
  if (!Number.isFinite(we)) we = ws + MIN_WORD_DUR;
  if (we <= ws + MIN_WORD_DUR) we = ws + MIN_WORD_DUR;

  ww.start = ws;
  ww.end = we;
  return ww;
}

function clampWordsToSegment(seg) {
  let s = sanitizeSegmentTimes(seg);
  const words = [...(s.words ?? [])].map((w) => ensureWordValid(w));

  for (let i = 0; i < words.length; i++) {
    let ws = words[i].start;
    let we = words[i].end;

    ws = clamp(ws, s.start, s.end - MIN_WORD_DUR);
    we = clamp(we, ws + MIN_WORD_DUR, s.end);

    words[i].start = ws;
    words[i].end = we;
  }

  s.words = words;
  s.text = rebuildSegmentText(s);
  return s;
}

// Word ripple: push neighbor only when crossing; cascade until no crossing
function rippleWordsOnCrossing(words, editedIndex, editedField) {
  const out = words.map((w) => ensureWordValid(w));
  const last = out.length - 1;

  if (editedField === "start") {
    for (let i = editedIndex; i > 0; i--) {
      const cur = out[i];
      const prev = { ...out[i - 1] };

      if (cur.start < prev.end) {
        prev.end = cur.start;

        if (prev.end <= prev.start + MIN_WORD_DUR) {
          prev.start = prev.end - MIN_WORD_DUR;
        }

        out[i - 1] = ensureWordValid(prev);
      } else {
        break;
      }
    }
  }

  if (editedField === "end") {
    for (let i = editedIndex; i < last; i++) {
      const cur = out[i];
      const next = { ...out[i + 1] };

      if (cur.end > next.start) {
        next.start = cur.end;

        if (next.end <= next.start + MIN_WORD_DUR) {
          next.end = next.start + MIN_WORD_DUR;
        }

        out[i + 1] = ensureWordValid(next);
      } else {
        break;
      }
    }
  }

  return out;
}

// Segment neighbor push: only when overlap
function rippleSegmentsOnCrossing(segs, idx) {
  const out = segs;

  if (idx > 0) {
    const cur = sanitizeSegmentTimes(out[idx]);
    let prev = sanitizeSegmentTimes(out[idx - 1]);

    if (cur.start < prev.end) {
      prev.end = cur.start;

      if (prev.end <= prev.start + MIN_SEG_DUR) {
        prev.start = Math.max(0, prev.end - MIN_SEG_DUR);
      }

      out[idx - 1] = clampWordsToSegment(prev);
    }
  }

  if (idx < out.length - 1) {
    const cur = sanitizeSegmentTimes(out[idx]);
    let next = sanitizeSegmentTimes(out[idx + 1]);

    if (cur.end > next.start) {
      next.start = cur.end;

      if (next.end <= next.start + MIN_SEG_DUR) {
        next.end = next.start + MIN_SEG_DUR;
      }

      out[idx + 1] = clampWordsToSegment(next);
    }
  }

  return out;
}

export default function SegmentEditor({
  subject, // MUST include: owner, language, author, title, type
  file, // narrator filename OR { file: "..." }
  segments, // optional
  editedSegments,
  setEditedSegments,
  editedSegment,
  setEditedSegment,
  audioref,
  audioSegment, // segment represented by the fragment currently loaded in audioref
  onNavigateSegment,
  onSave,
  onCancel,
}) {
  const [saving, setSaving] = useState(false);

  // UX toggles
  const [extendSegmentFromBoundaryWords, setExtendSegmentFromBoundaryWords] =
    useState(true);
  const [preRollMs, setPreRollMs] = useState(60);
  const [postRollMs, setPostRollMs] = useState(60);

  // Playback speed: 1.0, 0.5, 0.25
  const [playbackRate, setPlaybackRate] = useState(1.0);

  // Dirty + last saved
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSavedSnapshotRef = useRef("");

  // Undo/Redo history
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyTick, setHistoryTick] = useState(0);

  // Focus for nudging
  const [focusedWordIndex, setFocusedWordIndex] = useState(null);

  // Playback state
  const rafRef = useRef(null);
  const [playing, setPlaying] = useState({
    mode: "none",
    wordIndex: null,
  });

  const currentIndex = editedSegment?.id ?? 0;
  const segWords = editedSegment?.words ?? [];
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < (editedSegments?.length ?? 0) - 1;

  // The parent fragment is used initially. Once /edit_narrator returns a
  // temporary preview WAV for changed sentence boundaries, that exact preview
  // becomes the audio timeline used by the editor and pitch chart.
  const [editorAudioSegment, setEditorAudioSegment] = useState(null);
  const effectiveAudioSegment = editorAudioSegment ?? audioSegment;

  useEffect(() => {
    setEditorAudioSegment(null);
  }, [
    currentIndex,
    audioSegment?.id,
    audioSegment?.start,
    audioSegment?.end,
  ]);

  // The audio element now contains only one segment fragment. Its currentTime
  // starts at zero, while edited segment/word timestamps remain absolute.
  const fragmentStart = useMemo(() => {
    const value = Number(
      effectiveAudioSegment?.start ?? editedSegment?.start ?? 0
    );
    return Number.isFinite(value) ? value : 0;
  }, [effectiveAudioSegment?.start, editedSegment?.start]);

  const fragmentEnd = useMemo(() => {
    const value = Number(effectiveAudioSegment?.end);
    return Number.isFinite(value) ? value : null;
  }, [effectiveAudioSegment?.end]);

  const fragmentStartRef = useRef(fragmentStart);
  fragmentStartRef.current = fragmentStart;

  const chartAudioSourceRef = useRef(null);
  const chartAudioProxyRef = useRef(null);

  // PitchEditorChart was written for a full source audio element. Keep that
  // contract without modifying the chart: reads expose absolute currentTime,
  // while writes are translated back to the zero-based fragment timeline.
  const pitchChartAudioRef = useMemo(() => {
    const wrapper = {};

    Object.defineProperty(wrapper, "current", {
      enumerable: true,
      get() {
        const source = audioref?.current;
        if (!source) return null;

        if (
          chartAudioSourceRef.current !== source ||
          !chartAudioProxyRef.current
        ) {
          chartAudioSourceRef.current = source;
          chartAudioProxyRef.current = new Proxy(source, {
            get(target, prop) {
              if (prop === "currentTime") {
                return Number(target.currentTime || 0) + fragmentStartRef.current;
              }

              if (prop === "duration") {
                const duration = Number(target.duration);
                return Number.isFinite(duration)
                  ? fragmentStartRef.current + duration
                  : duration;
              }

              const value = Reflect.get(target, prop, target);
              return typeof value === "function" ? value.bind(target) : value;
            },

            set(target, prop, value) {
              if (prop === "currentTime") {
                const absoluteTime = Number(value);
                target.currentTime = Number.isFinite(absoluteTime)
                  ? Math.max(0, absoluteTime - fragmentStartRef.current)
                  : 0;
                return true;
              }

              return Reflect.set(target, prop, value, target);
            },
          });
        }

        return chartAudioProxyRef.current;
      },
    });

    return wrapper;
  }, [audioref]);

  const loadedFragmentMatchesEditedSegment = useMemo(() => {
    if (!effectiveAudioSegment || !editedSegment) return true;

    if (
      effectiveAudioSegment.id != null &&
      editedSegment.id != null &&
      Number(effectiveAudioSegment.id) !== Number(editedSegment.id)
    ) {
      return false;
    }

    const loadedStart = Number(effectiveAudioSegment.start);
    const loadedEnd = Number(effectiveAudioSegment.end);
    const editedStart = Number(editedSegment.start);
    const editedEnd = Number(editedSegment.end);

    if (
      Number.isFinite(loadedStart) &&
      Number.isFinite(loadedEnd) &&
      Number.isFinite(editedStart) &&
      Number.isFinite(editedEnd)
    ) {
      return (
        Math.abs(loadedStart - editedStart) <= 0.001 &&
        Math.abs(loadedEnd - editedEnd) <= 0.001
      );
    }

    return true;
  }, [effectiveAudioSegment, editedSegment]);

  const getFragmentDuration = (audio) => {
    const audioDuration = Number(audio?.duration);

    if (Number.isFinite(audioDuration) && audioDuration > 0) {
      return audioDuration;
    }

    if (fragmentEnd != null) {
      return Math.max(0, fragmentEnd - fragmentStart);
    }

    return Math.max(
      0,
      Number(editedSegment?.end ?? fragmentStart) - fragmentStart
    );
  };

  const absoluteToFragmentTime = (absoluteTime) =>
    Number(absoluteTime) - fragmentStart;

  // ----- Commit helper -----
  const commitSegments = (newSegments, nextSelectedIndex = null) => {
    setEditedSegments(newSegments);

    const idx =
      nextSelectedIndex != null
        ? nextSelectedIndex
        : editedSegment?.id != null
          ? editedSegment.id
          : 0;

    setEditedSegment(
      newSegments[idx] ?? newSegments[newSegments.length - 1] ?? null
    );
  };

  // ----- Undo/Redo -----
  const bumpHistoryTick = () => setHistoryTick((x) => x + 1);

  const pushHistory = () => {
    undoStackRef.current.push(deepCloneSegments(editedSegments));

    if (undoStackRef.current.length > 100) {
      undoStackRef.current.shift();
    }

    redoStackRef.current = [];
    bumpHistoryTick();
  };

  const doUndo = () => {
    if (undoStackRef.current.length === 0) return;

    const prev = undoStackRef.current.pop();
    redoStackRef.current.push(deepCloneSegments(editedSegments));

    setEditedSegments(prev);
    setEditedSegment(prev[currentIndex] ?? prev[prev.length - 1] ?? null);
    bumpHistoryTick();
  };

  const doRedo = () => {
    if (redoStackRef.current.length === 0) return;

    const next = redoStackRef.current.pop();
    undoStackRef.current.push(deepCloneSegments(editedSegments));

    setEditedSegments(next);
    setEditedSegment(next[currentIndex] ?? next[next.length - 1] ?? null);
    bumpHistoryTick();
  };

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  // ----- Dirty tracking -----
  const currentSnapshot = useMemo(() => {
    try {
      return JSON.stringify(editedSegments ?? []);
    } catch {
      return "";
    }
  }, [editedSegments]);

  const isDirty = currentSnapshot !== lastSavedSnapshotRef.current;

  useEffect(() => {
    if (!lastSavedSnapshotRef.current && (editedSegments?.length ?? 0) > 0) {
      lastSavedSnapshotRef.current = currentSnapshot;
      setLastSavedAt(new Date());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSegments]);

  // ============================
  // Accurate playback (RAF)
  // ============================
  const cancelRAF = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = null;
  };

  const stopPlayback = () => {
    const audio = audioref?.current;

    cancelRAF();

    if (audio) {
      audio.pause();
    }

    setPlaying({
      mode: "none",
      wordIndex: null,
    });
  };

  // Make sure audio rate always matches current speed
  useEffect(() => {
    const audio = audioref?.current;

    if (audio) {
      audio.playbackRate = playbackRate;
    }
  }, [audioref, playbackRate]);

  const playRangeOnce = (
    start,
    end,
    {
      mode = "none",
      wordIndex = null,
      preMs = preRollMs,
      postMs = postRollMs,
    } = {}
  ) => {
    const audio = audioref?.current;

    if (!audio || !loadedFragmentMatchesEditedSegment) return;
    if (!isFiniteNum(start) || !isFiniteNum(end)) return;

    const pre = Math.max(0, Number(preMs) / 1000);
    const post = Math.max(0, Number(postMs) / 1000);
    const maxTime = getFragmentDuration(audio);

    const s = clamp(
      absoluteToFragmentTime(Number(start) - pre),
      0,
      maxTime
    );
    const e = clamp(
      absoluteToFragmentTime(Number(end) + post),
      s,
      maxTime
    );

    if (e <= s) return;

    stopPlayback();

    audio.playbackRate = playbackRate;
    audio.currentTime = s;

    const tick = () => {
      if (!audioref?.current) return;

      const a = audioref.current;

      if (a.currentTime >= e) {
        a.pause();
        a.currentTime = s;
        cancelRAF();

        setPlaying({
          mode: "none",
          wordIndex: null,
        });

        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    audio.play().catch((error) => {
      console.error("Segment playback failed:", error);
      stopPlayback();
    });

    setPlaying({
      mode,
      wordIndex,
    });

    rafRef.current = requestAnimationFrame(tick);
  };

  const startLoopRange = (
    start,
    end,
    {
      mode = "word",
      wordIndex = null,
      preMs = preRollMs,
      postMs = postRollMs,
    } = {}
  ) => {
    const audio = audioref?.current;

    if (!audio || !loadedFragmentMatchesEditedSegment) return;
    if (!isFiniteNum(start) || !isFiniteNum(end)) return;

    const pre = Math.max(0, Number(preMs) / 1000);
    const post = Math.max(0, Number(postMs) / 1000);
    const maxTime = getFragmentDuration(audio);

    const s = clamp(
      absoluteToFragmentTime(Number(start) - pre),
      0,
      maxTime
    );
    const e = clamp(
      absoluteToFragmentTime(Number(end) + post),
      s,
      maxTime
    );

    if (e <= s) return;

    stopPlayback();

    audio.playbackRate = playbackRate;
    audio.currentTime = s;

    const epsilon = 0.002;

    const tick = () => {
      if (!audioref?.current) return;

      const a = audioref.current;

      if (a.currentTime >= e) {
        a.currentTime = Math.min(s + epsilon, e);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    audio.play().catch((error) => {
      console.error("Word loop playback failed:", error);
      stopPlayback();
    });

    setPlaying({
      mode,
      wordIndex,
    });

    rafRef.current = requestAnimationFrame(tick);
  };

  // ----- Keyboard shortcuts -----
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName ?? "").toLowerCase();
      const isTyping = tag === "input" || tag === "textarea";
      const meta = e.ctrlKey || e.metaKey;

      if (meta && e.key.toLowerCase() === "z") {
        e.preventDefault();

        if (e.shiftKey) {
          doRedo();
        } else {
          doUndo();
        }

        return;
      }

      if (meta && e.key.toLowerCase() === "y") {
        e.preventDefault();
        doRedo();
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        stopPlayback();
        return;
      }

      if (!editedSegment) return;

      if (!isTyping && (e.key === "1" || e.key === "2" || e.key === "3")) {
        e.preventDefault();

        if (e.key === "1") setPlaybackRate(1);
        if (e.key === "2") setPlaybackRate(0.5);
        if (e.key === "3") setPlaybackRate(0.25);

        return;
      }

      if (isTyping) return;

      if (focusedWordIndex != null && (e.key === "[" || e.key === "]")) {
        e.preventDefault();

        const step = e.shiftKey ? 0.1 : 0.01;
        const w = editedSegment.words?.[focusedWordIndex];

        if (!w) return;

        if (e.key === "[") {
          updateWordTime(
            currentIndex,
            focusedWordIndex,
            "start",
            Number(w.start) - step
          );
        } else {
          updateWordTime(
            currentIndex,
            focusedWordIndex,
            "end",
            Number(w.end) + step
          );
        }
      }

      if (e.code === "Space") {
        e.preventDefault();
        playSegmentOnce();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editedSegment,
    focusedWordIndex,
    currentIndex,
    preRollMs,
    postRollMs,
    extendSegmentFromBoundaryWords,
    historyTick,
    playbackRate,
    fragmentStart,
    loadedFragmentMatchesEditedSegment,
  ]);

  // ============================
  // Narrator pitch for EDITOR
  // ============================
  const [narrPitch, setNarrPitch] = useState(null);
  const [narrIntensity, setNarrIntensity] = useState(null);
  const [narrDeriv, setNarrDeriv] = useState(null);
  const [pitchLoading, setPitchLoading] = useState(false);
  const [pitchError, setPitchError] = useState(null);

  const narratorFileName = useMemo(() => {
    return typeof file === "string" ? file : file?.file ?? "";
  }, [file]);

  const segKey = useMemo(() => {
    if (!editedSegment) return "";

    const start = Number(editedSegment.start);
    const end = Number(editedSegment.end);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return "";
    }

    return `${start}-${end}`;
  }, [editedSegment?.start, editedSegment?.end]);

  useEffect(() => {
    if (!editedSegment || !segKey) return undefined;

    if (
      !subject?.owner ||
      !subject?.language ||
      !subject?.author ||
      !subject?.title ||
      !subject?.type ||
      !narratorFileName
    ) {
      return undefined;
    }

    let active = true;
    const controller = new AbortController();

    const fetchNarratorPitch = async () => {
      setPitchLoading(true);
      setPitchError(null);

      const token = localStorage.getItem("token");
      const encodePathPart = (value) =>
        encodeURIComponent(String(value));

      const url =
        `${process.env.REACT_APP_USER_API_URL}/analysis/edit_narrator/` +
        `${encodePathPart(subject.owner)}/` +
        `${encodePathPart(subject.language)}/` +
        `${encodePathPart(subject.author)}/` +
        `${encodePathPart(subject.title)}/` +
        `${encodePathPart(narratorFileName)}/` +
        `${encodePathPart(editedSegment.start)}/` +
        `${encodePathPart(editedSegment.end)}/` +
        `${encodePathPart(subject.type)}`;

      try {
        const formData = new FormData();
        formData.append("text", editedSegment.text || "");

        const res = await axios.post(url, formData, {
          signal: controller.signal,
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        });

        if (!active) return;

        const narr = res.data?.narrator_intonation;
        const block = narr?.intensity;

        setNarrPitch(block?.pitch || null);
        setNarrDeriv(block?.pitch_1st_derivative || null);
        setNarrIntensity(block?.intensity || null);

        const returnedSegment = narr?.segment || {};
        const returnedStart = Number(
          returnedSegment.start ?? editedSegment.start
        );
        const returnedEnd = Number(
          returnedSegment.end ?? editedSegment.end
        );
        const fragmentAudioUrl =
          narr?.audio_url ||
          narr?.fragment?.url ||
          res.data?.segment_audio_url;

        if (
          fragmentAudioUrl &&
          Number.isFinite(returnedStart) &&
          Number.isFinite(returnedEnd)
        ) {
          let resolvedAudioUrl = fragmentAudioUrl;

          try {
            resolvedAudioUrl = new URL(
              fragmentAudioUrl,
              process.env.REACT_APP_USER_API_URL || window.location.origin
            ).toString();
          } catch {
            // Keep the backend value when URL resolution is unavailable.
          }

          const audio = audioref?.current;
          if (audio) {
            cancelRAF();
            audio.pause();

            if (audio.src !== resolvedAudioUrl) {
              audio.src = resolvedAudioUrl;
              audio.load();
            }

            audio.currentTime = 0;
            audio.playbackRate = playbackRate;
            setPlaying({ mode: "none", wordIndex: null });
          }

          setEditorAudioSegment({
            id: editedSegment.id,
            start: returnedStart,
            end: returnedEnd,
            url: resolvedAudioUrl,
            temporary: Boolean(res.data?.temporary ?? narr?.temporary),
            cacheStatus: res.data?.cache_status || null,
          });
        }
      } catch (error) {
        const cancelled =
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError" ||
          axios.isCancel?.(error);

        if (cancelled) return;

        console.error("Editor narrator pitch fetch failed:", error);

        if (!active) return;

        setNarrPitch(null);
        setNarrDeriv(null);
        setNarrIntensity(null);

        const backendMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.error;

        setPitchError(
          backendMessage ||
            "Failed to load narrator pitch for this sentence."
        );
      } finally {
        if (active) {
          setPitchLoading(false);
        }
      }
    };

    fetchNarratorPitch();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    segKey,
    subject?.owner,
    subject?.language,
    subject?.author,
    subject?.title,
    subject?.type,
    narratorFileName,
    audioref,
  ]);

  // ============================
  // Editing handlers
  // ============================
  const updateSegmentTime = (segmentIndex, field, rawValue) => {
    const t = Number(rawValue);

    if (!Number.isFinite(t)) return;

    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);

    if (!segs[segmentIndex]) return;

    let seg = sanitizeSegmentTimes(segs[segmentIndex]);
    seg[field] = t;
    seg = sanitizeSegmentTimes(seg);

    seg = clampWordsToSegment(seg);
    segs[segmentIndex] = seg;

    rippleSegmentsOnCrossing(segs, segmentIndex);
    commitSegments(segs);
  };

  const updateWordText = (segmentIndex, wordIndex, text) => {
    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);
    const seg0 = segs[segmentIndex];

    if (!seg0) return;

    const seg = { ...seg0 };
    const words = (seg.words ?? []).map((w) => ({ ...w }));

    if (!words[wordIndex]) return;

    words[wordIndex].text = text;
    seg.words = words;
    seg.text = rebuildSegmentText(seg);

    segs[segmentIndex] = seg;
    commitSegments(segs);
  };

  const updateWordTime = (
    segmentIndex,
    wordIndex,
    field,
    rawValue
  ) => {
    const t = Number(rawValue);

    if (!Number.isFinite(t)) return;

    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);
    const seg0 = segs[segmentIndex];

    if (!seg0) return;

    let seg = sanitizeSegmentTimes(seg0);
    let words = (seg.words ?? []).map((w) => ensureWordValid(w));

    if (!words[wordIndex]) return;

    const isFirst = wordIndex === 0;
    const isLast = wordIndex === words.length - 1;

    let w = {
      ...words[wordIndex],
      [field]: t,
    };

    w = ensureWordValid(w);

    if (w.end <= w.start + MIN_WORD_DUR) {
      if (field === "start") {
        w.end = w.start + MIN_WORD_DUR;
      } else {
        w.start = w.end - MIN_WORD_DUR;
      }
    }

    if (
      !(
        isFirst &&
        field === "start" &&
        w.start < seg.start &&
        extendSegmentFromBoundaryWords
      )
    ) {
      w.start = clamp(
        w.start,
        seg.start,
        seg.end - MIN_WORD_DUR
      );
    }

    if (
      !(
        isLast &&
        field === "end" &&
        w.end > seg.end &&
        extendSegmentFromBoundaryWords
      )
    ) {
      w.end = clamp(
        w.end,
        w.start + MIN_WORD_DUR,
        seg.end
      );
    }

    words[wordIndex] = ensureWordValid(w);
    words = rippleWordsOnCrossing(words, wordIndex, field);

    seg.words = words;

    let changedSegStart = false;
    let changedSegEnd = false;

    if (extendSegmentFromBoundaryWords && words.length > 0) {
      const first = words[0];
      const last = words[words.length - 1];

      if (first.start < seg.start) {
        seg.start = first.start;
        changedSegStart = true;
      }

      if (last.end > seg.end) {
        seg.end = last.end;
        changedSegEnd = true;
      }

      seg = sanitizeSegmentTimes(seg);
    }

    seg = clampWordsToSegment(seg);
    segs[segmentIndex] = seg;

    if (changedSegStart || changedSegEnd) {
      rippleSegmentsOnCrossing(segs, segmentIndex);
    }

    commitSegments(segs);
  };

  // Drag boundary between word i and i+1
  const setWordBoundaryAbs = (
    segmentIndex,
    boundaryIndex,
    newAbsTime
  ) => {
    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);
    const seg0 = segs[segmentIndex];

    if (!seg0) return;

    let seg = sanitizeSegmentTimes(seg0);
    let words = (seg.words ?? []).map((w) => ensureWordValid(w));

    const a = words[boundaryIndex];
    const b0 = words[boundaryIndex + 1];

    if (!a || !b0) return;

    const minT = Number(a.start) + MIN_WORD_DUR;
    let t = Number(newAbsTime);

    if (!Number.isFinite(t)) return;

    let segRightLimit = Number(seg.end) - MIN_WORD_DUR;
    t = clamp(t, minT, segRightLimit);

    let b = { ...b0 };

    const needBEnd = t + MIN_WORD_DUR;

    if (needBEnd > Number(b.end)) {
      let newBEnd = needBEnd;

      if (newBEnd > seg.end) {
        if (extendSegmentFromBoundaryWords) {
          seg.end = newBEnd;
          seg = sanitizeSegmentTimes(seg);

          segRightLimit = Number(seg.end) - MIN_WORD_DUR;
          t = clamp(t, minT, segRightLimit);
          newBEnd = Math.max(newBEnd, t + MIN_WORD_DUR);
        } else {
          newBEnd = seg.end;
          t = clamp(t, minT, newBEnd - MIN_WORD_DUR);
        }
      }

      b.end = newBEnd;
    }

    words[boundaryIndex] = ensureWordValid({
      ...a,
      end: t,
    });

    words[boundaryIndex + 1] = ensureWordValid({
      ...b,
      start: t,
    });

    words = rippleWordsOnCrossing(words, boundaryIndex, "end");
    words = rippleWordsOnCrossing(
      words,
      boundaryIndex + 1,
      "start"
    );

    const segNext = clampWordsToSegment({
      ...seg,
      words,
    });

    segs[segmentIndex] = segNext;
    commitSegments(segs);
  };

  const addWordDirectional = (
    segmentIndex,
    insertAtIndex,
    direction
  ) => {
    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);
    const seg0 = segs[segmentIndex];

    if (!seg0) return;

    let seg = sanitizeSegmentTimes(seg0);
    const words = (seg.words ?? []).map((w) => ensureWordValid(w));
    const idx = clamp(insertAtIndex, 0, words.length);

    const prev = words[idx - 1];
    const next = words[idx];

    let startGuess;
    let endGuess;

    if (direction === "before") {
      const endAnchor =
        next && isFiniteNum(next.start)
          ? Number(next.start)
          : seg.end;

      const prevEnd =
        prev && isFiniteNum(prev.end)
          ? Number(prev.end)
          : seg.start;

      endGuess = clamp(
        endAnchor,
        seg.start + MIN_WORD_DUR,
        seg.end
      );

      startGuess = Math.max(
        prevEnd,
        endGuess - DEFAULT_NEW_WORD_LEN
      );

      startGuess = clamp(
        startGuess,
        seg.start,
        endGuess - MIN_WORD_DUR
      );
    } else {
      const startAnchor =
        prev && isFiniteNum(prev.end)
          ? Number(prev.end)
          : seg.start;

      const nextStart =
        next && isFiniteNum(next.start)
          ? Number(next.start)
          : seg.end;

      startGuess = clamp(
        startAnchor,
        seg.start,
        seg.end - MIN_WORD_DUR
      );

      endGuess = Math.min(
        startGuess + DEFAULT_NEW_WORD_LEN,
        nextStart,
        seg.end
      );

      endGuess = Math.max(
        endGuess,
        startGuess + MIN_WORD_DUR
      );
    }

    const newWord = {
      text: "",
      start: startGuess,
      end: endGuess,
      confidence: 1,
    };

    const nextWords = [...words];
    nextWords.splice(idx, 0, newWord);

    seg.words = nextWords;
    seg = clampWordsToSegment(seg);

    segs[segmentIndex] = seg;
    commitSegments(segs);
  };

  const removeWord = (segmentIndex, wordIndex) => {
    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);
    const seg0 = segs[segmentIndex];

    if (!seg0) return;

    let seg = { ...seg0 };
    const words = (seg.words ?? []).map((w) => ({ ...w }));

    if (!words[wordIndex]) return;

    words.splice(wordIndex, 1);

    seg.words = words;
    seg.text = rebuildSegmentText(seg);
    seg = clampWordsToSegment(seg);

    segs[segmentIndex] = seg;
    commitSegments(segs);
  };

  const splitSegmentAtWord = (segmentIndex, wordIndex) => {
    const seg0 = editedSegments?.[segmentIndex];

    if (!seg0) return;

    const originalWords = (seg0.words ?? []).map((w) =>
      ensureWordValid(w)
    );

    if (
      wordIndex <= 0 ||
      wordIndex >= originalWords.length
    ) {
      return;
    }

    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);
    const seg = sanitizeSegmentTimes(segs[segmentIndex]);
    const words = (seg.words ?? []).map((w) => ensureWordValid(w));

    const leftWords = words.slice(0, wordIndex);
    const rightWords = words.slice(wordIndex);

    if (leftWords.length === 0 || rightWords.length === 0) {
      return;
    }

    let leftSeg = {
      ...seg,
      start: Number(seg.start),
      end: Math.max(
        Number(seg.start) + MIN_SEG_DUR,
        Number(leftWords[leftWords.length - 1].end)
      ),
      words: leftWords,
    };

    let rightSeg = {
      ...seg,
      start: Number(rightWords[0].start),
      end: Math.max(
        Number(rightWords[0].start) + MIN_SEG_DUR,
        Number(seg.end)
      ),
      words: rightWords,
    };

    leftSeg = clampWordsToSegment(leftSeg);
    rightSeg = clampWordsToSegment(rightSeg);

    segs.splice(segmentIndex, 1, leftSeg, rightSeg);

    const renum = segs.map((s, i) => ({
      ...s,
      id: i,
      text: rebuildSegmentText(s),
    }));

    rippleSegmentsOnCrossing(renum, segmentIndex);
    rippleSegmentsOnCrossing(renum, segmentIndex + 1);

    commitSegments(renum, segmentIndex + 1);
  };

  const deleteSegment = (segmentIndex) => {
    if (!editedSegments?.[segmentIndex]) return;

    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);
    segs.splice(segmentIndex, 1);

    if (segs.length === 0) {
      setEditedSegments([]);
      setEditedSegment(null);
      return;
    }

    const renum = segs.map((s, i) => ({
      ...s,
      id: i,
    }));

    const left = segmentIndex - 1;
    const right = segmentIndex;

    if (left >= 0 && right < renum.length) {
      const prev = sanitizeSegmentTimes(renum[left]);
      const next = sanitizeSegmentTimes(renum[right]);

      if (prev.end > next.start) {
        next.start = prev.end;
        renum[right] = clampWordsToSegment(next);
      }
    }

    const newIdx = Math.min(segmentIndex, renum.length - 1);
    commitSegments(renum, newIdx);
  };

  const mergeWithNeighbor = (
    segmentIndex,
    direction
  ) => {
    const otherIndex =
      direction === "prev"
        ? segmentIndex - 1
        : segmentIndex + 1;

    if (
      !editedSegments?.[segmentIndex] ||
      !editedSegments?.[otherIndex]
    ) {
      return;
    }

    stopPlayback();
    pushHistory();

    const segs = deepCloneSegments(editedSegments);

    const aIndex =
      direction === "prev"
        ? otherIndex
        : segmentIndex;

    const bIndex =
      direction === "prev"
        ? segmentIndex
        : otherIndex;

    const a = sanitizeSegmentTimes(segs[aIndex]);
    const b = sanitizeSegmentTimes(segs[bIndex]);

    const aWords = (a.words ?? []).map((w) => ensureWordValid(w));
    const bWords = (b.words ?? []).map((w) => ensureWordValid(w));
    const mergedWords = [...aWords, ...bWords];

    let merged = {
      ...a,
      start: Math.min(a.start, b.start),
      end: Math.max(a.end, b.end),
      words: mergedWords,
    };

    if (Array.isArray(a.tokens) && Array.isArray(b.tokens)) {
      merged.tokens = [...a.tokens, ...b.tokens];
    }

    merged.text = rebuildSegmentText(merged);
    merged = sanitizeSegmentTimes(merged);

    segs.splice(bIndex, 1);
    segs[aIndex] = merged;

    const renum = segs.map((s, i) => ({
      ...s,
      id: i,
    }));

    rippleSegmentsOnCrossing(renum, aIndex);
    commitSegments(renum, aIndex);
  };

  const navigateEditorSegment = (direction) => {
    const delta = direction === "prev" ? -1 : 1;
    const nextIndex = currentIndex + delta;
    const nextSegment = editedSegments?.[nextIndex];

    if (!nextSegment) return;

    stopPlayback();

    if (audioref?.current) {
      audioref.current.currentTime = 0;
    }

    setEditedSegment(nextSegment);
    onNavigateSegment?.(direction, nextSegment);
  };

  // ----- Playback wrappers -----
  const playSegmentOnce = () => {
    if (!editedSegment) return;

    playRangeOnce(
      editedSegment.start,
      editedSegment.end,
      { mode: "segment" }
    );
  };

  const playWordOnce = (word, wordIndex) => {
  playRangeOnce(word.start, word.end, {
    mode: "word",
    wordIndex,
    preMs: 0,
    postMs: 0,
  });
};

  const onWordPlayMouseDown = (word, wordIndex) => {
    if (!word) return;

    startLoopRange(word.start, word.end, {
      mode: "word",
      wordIndex,
      preMs: 0,
      postMs: 0,
    });
  };

  // ----- Save -----
  const saveEditedSegments = async () => {
    setSaving(true);

    try {
      await onSave(editedSegments);
      lastSavedSnapshotRef.current = currentSnapshot;
      setLastSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      stopPlayback();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!editedSegments || !editedSegment) {
    return null;
  }

  return (
    <div className="flex flex-col min-h-0 max-h-[78vh] overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-sm">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Segment Editor
              </h3>

              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  isDirty
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {isDirty ? "Unsaved" : "Saved"}
              </span>

              {lastSavedAt && (
                <span className="text-xs text-gray-500">
                  last saved {lastSavedAt.toLocaleTimeString()}
                </span>
              )}
            </div>

            <div className="text-xs text-gray-500 mt-1">
              Space=play segment • Hold ▶ loops word • [ / ] nudge focused word
              (Shift=big) • Esc=stop • Ctrl/Cmd+Z undo
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={doUndo}
              disabled={!canUndo}
              className="px-3 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo (Ctrl/Cmd+Z)"
            >
              Undo
            </button>

            <button
              type="button"
              onClick={doRedo}
              disabled={!canRedo}
              className="px-3 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo (Ctrl/Cmd+Shift+Z)"
            >
              Redo
            </button>

            <button
              type="button"
              onClick={() =>
                mergeWithNeighbor(currentIndex, "prev")
              }
              disabled={!canPrev}
              className="px-3 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Merge with previous segment"
            >
              Merge ↑
            </button>

            <button
              type="button"
              onClick={() =>
                mergeWithNeighbor(currentIndex, "next")
              }
              disabled={!canNext}
              className="px-3 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Merge with next segment"
            >
              Merge ↓
            </button>

            <button
              type="button"
              onClick={() => deleteSegment(currentIndex)}
              className="px-3 py-2 rounded-lg text-sm bg-red-500 hover:bg-red-600 text-white"
              title="Delete this segment"
            >
              Delete
            </button>

            <button
              type="button"
              onClick={() => {
                stopPlayback();
                onCancel();
              }}
              className="px-3 py-2 rounded-lg text-sm bg-gray-500 hover:bg-gray-600 text-white"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={saveEditedSegments}
              disabled={saving}
              className={`px-3 py-2 rounded-lg text-sm ${
                saving
                  ? "bg-gray-300 text-gray-700 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>

        {/* Settings row */}
        <div className="mt-3 flex flex-wrap gap-4 items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={extendSegmentFromBoundaryWords}
              onChange={(e) =>
                setExtendSegmentFromBoundaryWords(e.target.checked)
              }
            />
            Boundary words extend segment only when crossing
          </label>

          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-gray-500">Speed</span>

            <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden">
              {[1, 0.5, 0.25].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setPlaybackRate(r)}
                  className={`px-2.5 py-1 text-sm ${
                    playbackRate === r
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                  title={`Set speed to ${r}x`}
                >
                  {r}×
                </button>
              ))}
            </div>

            <span className="text-xs text-gray-500 ml-1">
              (keys: 1/2/3)
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-gray-500">Pre</span>

            <input
              type="number"
              step="10"
              value={preRollMs}
              onChange={(e) =>
                setPreRollMs(
                  Math.max(0, Number(e.target.value) || 0)
                )
              }
              className="w-20 border border-gray-300 rounded-lg px-2 py-1"
            />

            <span className="text-gray-500">ms</span>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-gray-500">Post</span>

            <input
              type="number"
              step="10"
              value={postRollMs}
              onChange={(e) =>
                setPostRollMs(
                  Math.max(0, Number(e.target.value) || 0)
                )
              }
              className="w-20 border border-gray-300 rounded-lg px-2 py-1"
            />

            <span className="text-gray-500">ms</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 py-4 space-y-4">
        {/* Pitch + intensity editor chart */}
        <div className="space-y-2">
          {pitchLoading && (
            <div className="text-sm text-gray-500">
              Loading untrimmed narrator pitch and intensity…
            </div>
          )}

          {pitchError && (
            <div className="text-sm text-red-600">
              {pitchError}
            </div>
          )}

          {(narrPitch || narrIntensity) && (
            <PitchEditorChart
              pitch={narrPitch}
              intensity={narrIntensity}
              segment={editedSegment}
              audioRef={pitchChartAudioRef}
              onSetWordBoundaryAbs={(i, tAbs) =>
                setWordBoundaryAbs(currentIndex, i, tAbs)
              }
              onSetLastWordEndAbs={(tAbs) =>
                updateWordTime(
                  currentIndex,
                  segWords.length - 1,
                  "end",
                  tAbs
                )
              }
              onSetSegmentEndAbs={(tAbs) =>
                updateSegmentTime(
                  currentIndex,
                  "end",
                  tAbs
                )
              }
            />
          )}
        </div>

        {/* Segment navigation + play */}
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            Segment{" "}
            <span className="font-medium text-gray-900">
              {currentIndex + 1}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-900">
              {editedSegments.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`p-2 rounded-lg ${
                canPrev
                  ? "bg-gray-100 hover:bg-gray-200"
                  : "bg-gray-50 opacity-50 cursor-not-allowed"
              }`}
              disabled={!canPrev}
              onClick={() => navigateEditorSegment("prev")}
              title="Previous segment"
            >
              <SlControlRewind className="text-gray-700" />
            </button>

            <button
              type="button"
              className={`p-2 rounded-lg ${
                loadedFragmentMatchesEditedSegment
                  ? "bg-blue-100 hover:bg-blue-200"
                  : "bg-gray-50 opacity-50 cursor-not-allowed"
              }`}
              onClick={playSegmentOnce}
              disabled={!loadedFragmentMatchesEditedSegment}
              title={
                loadedFragmentMatchesEditedSegment
                  ? "Play"
                  : "Loading segment fragment"
              }
            >
              <SlControlPlay className="text-blue-700" />
            </button>

            <button
              type="button"
              className={`p-2 rounded-lg ${
                canNext
                  ? "bg-gray-100 hover:bg-gray-200"
                  : "bg-gray-50 opacity-50 cursor-not-allowed"
              }`}
              disabled={!canNext}
              onClick={() => navigateEditorSegment("next")}
              title="Next segment"
            >
              <SlControlForward className="text-gray-700" />
            </button>

            {playing.mode !== "none" && (
              <button
                type="button"
                className="ml-2 px-3 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200"
                onClick={stopPlayback}
              >
                Stop
              </button>
            )}
          </div>
        </div>

        {/* Segment time controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Start time
            </label>

            <input
              type="number"
              step="0.01"
              value={editedSegment.start}
              onChange={(e) =>
                updateSegmentTime(
                  currentIndex,
                  "start",
                  e.target.value
                )
              }
              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              End time
            </label>

            <input
              type="number"
              step="0.01"
              value={editedSegment.end}
              onChange={(e) =>
                updateSegmentTime(
                  currentIndex,
                  "end",
                  e.target.value
                )
              }
              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Words header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-900">
              Words
            </div>

            <div className="text-xs text-gray-500">
              Neighbors move only when you CROSS limits (overlap).
            </div>
          </div>

          <button
            type="button"
            onClick={() =>
              addWordDirectional(
                currentIndex,
                segWords.length,
                "after"
              )
            }
            className="px-3 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200"
          >
            + Add word
          </button>
        </div>

        {/* Words table */}
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600 grid grid-cols-[48px_1fr_48px_92px_92px_56px_44px_68px] gap-2 items-center">
            <div className="text-center">&lt;+</div>
            <div>Word</div>
            <div className="text-center">+&gt;</div>
            <div className="text-center">Start</div>
            <div className="text-center">End</div>
            <div className="text-center">Play</div>
            <div className="text-center">Del</div>
            <div className="text-center">Split</div>
          </div>

          <div className="divide-y divide-gray-200">
            {segWords.map((word, wordIndex) => {
              const isPlayingWord =
                playing.mode === "word" &&
                playing.wordIndex === wordIndex;

              const dur = Math.max(
                0,
                (Number(word.end) || 0) -
                  (Number(word.start) || 0)
              );

              return (
                <div
                  key={wordIndex}
                  className={`px-3 py-2 grid grid-cols-[48px_1fr_48px_92px_92px_56px_44px_68px] gap-2 items-center ${
                    isPlayingWord ? "bg-blue-50" : "bg-white"
                  }`}
                  onMouseDown={() =>
                    setFocusedWordIndex(wordIndex)
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      addWordDirectional(
                        currentIndex,
                        wordIndex,
                        "before"
                      )
                    }
                    className="h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs"
                    title="Insert BEFORE"
                  >
                    &lt;+
                  </button>

                  <div className="flex items-center gap-2 min-w-0">
                    <input
                      value={word.text ?? ""}
                      onChange={(e) =>
                        updateWordText(
                          currentIndex,
                          wordIndex,
                          e.target.value
                        )
                      }
                      className="w-full min-w-0 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="(empty)"
                    />

                    <div className="hidden lg:block text-xs text-gray-500 whitespace-nowrap">
                      dur {dur.toFixed(2)}s
                      {focusedWordIndex === wordIndex
                        ? " • focused"
                        : ""}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      addWordDirectional(
                        currentIndex,
                        wordIndex + 1,
                        "after"
                      )
                    }
                    className="h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs"
                    title="Insert AFTER"
                  >
                    +&gt;
                  </button>

                  <input
                    type="number"
                    step="0.01"
                    value={
                      isFiniteNum(word.start)
                        ? word.start
                        : editedSegment.start
                    }
                    onChange={(e) =>
                      updateWordTime(
                        currentIndex,
                        wordIndex,
                        "start",
                        e.target.value
                      )
                    }
                    className="h-8 border border-gray-300 rounded-lg px-2 text-sm text-center"
                    onFocus={() =>
                      setFocusedWordIndex(wordIndex)
                    }
                    title="Start"
                  />

                  <input
                    type="number"
                    step="0.01"
                    value={
                      isFiniteNum(word.end)
                        ? word.end
                        : Number(word.start) + MIN_WORD_DUR
                    }
                    onChange={(e) =>
                      updateWordTime(
                        currentIndex,
                        wordIndex,
                        "end",
                        e.target.value
                      )
                    }
                    className="h-8 border border-gray-300 rounded-lg px-2 text-sm text-center"
                    onFocus={() =>
                      setFocusedWordIndex(wordIndex)
                    }
                    title="End"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      playWordOnce(word, wordIndex)
                    }
                    onMouseDown={() =>
                      onWordPlayMouseDown(word, wordIndex)
                    }
                    onMouseUp={stopPlayback}
                    onMouseLeave={stopPlayback}
                    className="h-8 rounded-lg bg-blue-100 hover:bg-blue-200 text-sm"
                    title="Click: play once • Hold: loop"
                  >
                    ▶
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      removeWord(currentIndex, wordIndex)
                    }
                    className="h-8 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm"
                    title="Remove word"
                  >
                    ✕
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      splitSegmentAtWord(
                        currentIndex,
                        wordIndex
                      )
                    }
                    disabled={
                      wordIndex === 0 ||
                      segWords.length < 2
                    }
                    className="h-8 rounded-lg bg-amber-100 hover:bg-amber-200 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      wordIndex === 0
                        ? "Cannot split at the first word"
                        : "Start a new segment from this word"
                    }
                  >
                    Split
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Full text */}
        <div>
          <div className="text-sm font-medium text-gray-900 mb-1">
            Full text
          </div>

          <div className="p-3 bg-white border border-gray-200 rounded-2xl text-gray-800">
            {editedSegment.text}
          </div>
        </div>
      </div>
    </div>
  );
}